import { z } from "zod";

export const DocumentSourceEnum = z.enum([
  "current_control",
  "parent_control",
  // A work step's own document (`work_step_document`), the work-step analogue
  // of `current_control`. Only a work step session's documentation listing
  // produces this.
  "work_step",
  "session_supplementary",
]);

export type DocumentSource = z.infer<typeof DocumentSourceEnum>;

export const ChildTypeEnum = z.enum([
  "excel_screenshot",
  "text_screenshot",
  "excel_embedded_image",
  "video_screenshot",
  "msg_attachment",
  "step_card",
  "exception_rows_card",
]);
export type ChildType = z.infer<typeof ChildTypeEnum>;

export const FullDocumentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileType: z.string(),
  fileUrl: z.string(),
  state: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isParsed: z.boolean(),
  fileContentHash: z.string(),
  documentType: z.string().nullable(),
  parentDocumentId: z.string().nullable(),
  childType: ChildTypeEnum.nullable().optional(),
  lastProcessingAction: z.string().nullable(),
  excludeFromRecovery: z.boolean(),
  extra: z.record(z.unknown()),
});

export type FullDocument = z.infer<typeof FullDocumentSchema>;

// One row of a session's documentation listing, from either
// `GET /matching/control-session/{id}/documentation` or
// `GET /work-steps/work-step-session/{id}/documentation`. The two responses mix
// different entity-document shapes (`ControlDocument` vs `WorkStepDocument`)
// with the same `SessionSupplementaryDocument`, so every parent key is optional
// and `documentSource` is derived from which one is present.
export const BackendDocumentSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string(),
  controlId: z.string().uuid().nullable().optional(),
  // Nullable since main_0034: a supplementary document now hangs off either a
  // control session or a work step session, and carries null for the other.
  controlSessionId: z.string().uuid().nullable().optional(),
  workStepSessionId: z.string().uuid().nullable().optional(),
  workStepId: z.string().uuid().nullable().optional(),
  docType: z.string().nullable(),
  sourceSystem: z.string().nullable(),
  documentType: z.string().nullable().optional(),
  state: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  fullDocument: FullDocumentSchema,
});

export type BackendDocument = z.infer<typeof BackendDocumentSchema>;

// Transformed document with derived documentSource
export interface SessionDocument {
  id: string;
  documentId: string;
  documentSource: DocumentSource;
  docType: string | null;
  sourceSystem: string | null;
  documentType: string | null;
  childType?: ChildType | null;
  state: string;
  fileName: string;
  fileType: string;
  createdAt: string;
  updatedAt: string;
  fullDocument: FullDocument;
}

export const GetDocumentationResponseSchema = z.object({
  documents: z.array(BackendDocumentSchema),
  totalCount: z.number(),
});

export type GetDocumentationResponse = z.infer<
  typeof GetDocumentationResponseSchema
>;

export const UploadSupplementaryDocumentsResponseSchema = z.object({
  message: z.string(),
  document_count: z.number(),
  session_id: z.string().uuid(),
  documents: z.array(
    z.object({
      id: z.string(),
      file_name: z.string(),
      file_type: z.string(),
      state: z.string(),
    }),
  ),
});

export type UploadSupplementaryDocumentsResponse = z.infer<
  typeof UploadSupplementaryDocumentsResponseSchema
>;

export const DownloadDocumentResponseSchema = z.object({
  url: z.string(),
});

export type DownloadDocumentResponse = z.infer<
  typeof DownloadDocumentResponseSchema
>;
